<template>
  <div v-if="currentPlan" class="h-full w-full flex flex-col justify-between overflow-y-auto text-dark-500">
    <div class="w-full max-w-300 mx-auto md:max-w-full md:pl-100 pt-35 md:pt-67 md:pr-50 pb-50">
      <div class="flex items-center md:hidden text-app-color" @click="back">
        <i class="text-12 mr-10 icon-keyboard-arrow-left opacity-50" />
        <p class="text-opacity-80">Back</p>
      </div>

      <h2 class="text-app-color font-bold text-35 md:text-21">{{ title }}</h2>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-if="!isExpired || isTeamAccount" class="text-15" :class="isDarkMode ? 'text-app-color' : 'text-dark-500'" v-html="subTittle" />
      <p v-if="!isTeamAccount && isExpired" class="text-error">
        Your Pro plan has expired.
        <a
          class="text-main-color"
          :href="`https://helloivy.co/upgrade?userId=${user.id}`"
          target="_blank"
        >Upgrade</a>
      </p>

      <div v-if="isTrialPlan || isFreePlan" class="flex items-center text-15 mt-20">
        <Switcher v-model="billAnnually" :is-dark-mode="isDarkMode" class="max-w-300">
          <template #title0>
            <p>
              Bill yearly
              <span
                class="switcher__yearly__percent text-green-1000  text-12 font-medium rounded-6 ml-8 py-5 px-8"
                :class="isDarkMode ? 'bg-backdrop' : 'bg-grey-500'"
              >20%</span>
            </p>
          </template>
          <template #title1>
            Bill monthly
          </template>
        </Switcher>
      </div>

      <div class="flex flex-wrap mt-20">
        <template v-if="isTeamAccount">
          <TrialPlan v-if="isTrialPlan" class="mt-30 mr-30" @cancel="openConfirm" />
          <TeamsPlan :bill-annually="billAnnually" class="mt-30 md:mt-10" @cancel="openConfirm" />
        </template>
        <template v-else>
          <FreePlan class="mt-30 mr-30" @cancel="openConfirm" />
          <div class="w-full max-w-270">
            <ProPlan :bill-annually="billAnnually" class="mt-30 md:mt-10" />
            <div class="text-center">
              <ButtonModule
                v-if="showCancelBtn"
                text="Cancel subscription"
                class="text-14 py-15 text-app-color mr-20"
                @click="openConfirm"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <ConfirmModal
      ref="confirmRef"
      title="Delete account"
      confirm-btn-title="Confirm"
      confirm-btn-class="bg-link-color"
      @confirm="cancelSubscription"
    >
      <p class="text-14 mt-20 mb-25">
        Are you sure you want to cancel your subscription?
      </p>
    </ConfirmModal>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'
import { useStore } from 'vuex'
import { planService } from '@/services'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import ConfirmModal from '@/components/confirm/ConfirmModal'

import FreePlan from '@/components/plan/Free'
import ProPlan from '@/components/plan/Pro'
import TeamsPlan from '@/components/plan/Teams'
import TrialPlan from '@/components/plan/Trial'
import Switcher from '@/components/shared/Switcher'

export default defineComponent({
  components: { ButtonModule, ConfirmModal, FreePlan, ProPlan, TeamsPlan, TrialPlan, Switcher },

  props: {
    title: {
      type: String,
      default: 'Plans'
    }
  },

  emits: ['back'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()
    const billAnnually = ref(false)
    const confirmRef = ref(false)

    const user = computed(() => state.profile.user)
    const plans = computed(() => state.plan.plans)
    const subscription = computed(() => state.plan.subscription)
    const isFreePlan = computed(() => getters.isFreePlan)
    const isTrialPlan = computed(() => getters.isTrialPlan)
    const isDarkMode = computed(() => getters.isDarkMode)
    const currentPlan = computed(() => getters.currentPlan)
    const isExpired = computed(() => getters.isExpired)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const trialDaysLeft = computed(() => getters.trialDaysLeft)

    const subTittle = computed(() => {
      if (isTeamAccount.value) {
        return isTrialPlan.value
          ? `<p>You have <span class="font-bold">${trialDaysLeft.value} days</span> left on your 15-day free trial.</p>`
          : '<p>You\'re using hello ivy for teams.</p>'
      } else {
        return isFreePlan.value
          ? '<p>Upgrade to the pro plan for unlimited pages, spaces and storage</p>'
          : '<p>You\'re currently using the pro version.</p>'
      }
    })
    const showCancelBtn = computed(() => !subscription.value.isCancelled && subscription.value.planId > 1)

    function cancelSubscription () {
      planService.cancelSubscription().then(() => {
        commit('toggleSettingsModal', false)
        dispatch('getCurrentPlan')
        commit('setNotification', { isVisible: true, message: 'Subscription was successfully canceled', color: '' })
      })
    }

    function openConfirm () {
      confirmRef.value.open()
    }

    function back () {
      emit('back')
    }

    return {
      // ref
      billAnnually,
      confirmRef,
      isTeamAccount,
      // computed
      user,
      plans,
      subscription,
      isFreePlan,
      isDarkMode,
      currentPlan,
      isExpired,
      subTittle,
      showCancelBtn,
      isTrialPlan,
      // function
      cancelSubscription,
      openConfirm,
      back
    }
  }
})
</script>
